// Copyright (c) Microsoft. All rights reserved.

import {
    Caption1,
    Card,
    CardHeader,
    ProgressBar,
    Text,
    makeStyles,
    mergeClasses,
    shorthands,
    tokens,
} from '@fluentui/react-components';
import React, { useEffect, useState } from 'react';
import { IChatMessage } from '../../../libs/models/ChatMessage';
import { SharedStyles } from '../../../styles';
import { getFileIconByFileExtension } from '../tabs/DocumentsTab';
//import {PayloadAction } from '@reduxjs/toolkit';

const useClasses = makeStyles({
    root: {
        display: 'flex',
        flexDirection: 'column',
        ...shorthands.margin(tokens.spacingVerticalM, 0),
    },
    card: {
        height: 'fit-content',
        width: '275px',
        backgroundColor: tokens.colorNeutralBackground3,
        ...shorthands.gap(0),
        ...shorthands.margin(tokens.spacingVerticalXXS, 0),
        ...shorthands.padding(tokens.spacingVerticalXXS, 0),
    },
    cardCaption: {
        color: tokens.colorNeutralForeground2,
    },
    cardHeader: {
        ...shorthands.margin(0, tokens.spacingHorizontalS),
    },
    cardHeaderText: {
        fontSize: 'small',
        fontWeight: '500',
    },
    cardHeaderTextContainer: {
        ...SharedStyles.overflowEllipsis,
    },
    footer: {
        float: 'right',
        fontSize: 'small',
        fontWeight: '500',
        color: tokens.colorNeutralForegroundDisabled,
    },
    icon: {
        height: '32px',
        width: '32px',
    },
    floatLeft: {
        float: 'left',
    },
});

interface ChatHistoryDocumentContentProps {
    isMe: boolean;
    message: IChatMessage;
    onDocumentClick: (documentId: string) => void;
}

interface DocumentData {
    name: string;
    size: string;
    isUploaded: boolean;
    documentId: string;
    mimeType: string;
}

interface DocumentMessageContent {
    documents?: DocumentData[];
    urls?: DocumentData[];
}

export const ChatHistoryDocumentContent: React.FC<ChatHistoryDocumentContentProps> = ({
    isMe,
    message,
    onDocumentClick,
}) => {
    const classes = useClasses();
    const [document, setDocument] = useState<DocumentData | null>(null);

    useEffect(() => {
        try {
            const content = JSON.parse(message.content) as DocumentMessageContent;
            const documentData = content.documents?.[0] ?? content.urls?.[0];
            if (documentData) {
                setDocument(documentData);
            }
        } catch (error) {
            console.error('Error parsing chat history file item:', error);
            setDocument(null);
        }
    }, [message.content]);

    if (!document) {
        return null;
    }

    const messageId = message.id ?? 'unknown-message-id';
    const isClickable = message.isV2 && document.documentId;
    const footerClassName = isMe ? classes.footer : mergeClasses(classes.footer, classes.floatLeft);
    const statusMessage = document.isUploaded ? 'Success: memory established' : 'Failed: memory not established';

    const handleClick = () => {
        if (isClickable) {
            onDocumentClick(document.documentId);
        }
    };

    return (
        <div
            className={classes.root}
            key={`${messageId}-document-${document.documentId}`}
            onClick={handleClick}
            style={{ cursor: isClickable ? 'pointer' : 'default' }}
        >
            <Card appearance="filled-alternative" className={classes.card}>
                <CardHeader
                    className={classes.cardHeader}
                    image={getFileIconByFileExtension(document.name, { className: classes.icon })}
                    header={{
                        className: classes.cardHeaderTextContainer,
                        children: (
                            <Text className={classes.cardHeaderText} title={document.name}>
                                {document.name}
                            </Text>
                        ),
                    }}
                    description={
                        <Caption1 block className={classes.cardCaption}>
                            {document.size}
                        </Caption1>
                    }
                />
                <ProgressBar thickness="large" color={document.isUploaded ? 'success' : 'error'} value={1} />
            </Card>
            <span className={footerClassName}>{statusMessage}</span>
        </div>
    );
};
